import React, { useState, useCallback, useMemo, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import GridSummary from "aldoo-ra/GridSummary"
import Tabs from "aldoo-ra/Tabs"
import DataTable from "aldoo-ra/DataTable"
import Checkbox from "aldoo-ra/Checkbox"
import Accordion from "aldoo-ra/Accordion"
import Localization from "aldoo-ra/Localization"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { RemoveUserFromLicense, DeactivateDevice, GetLicenseUsers, GetLicenseDevices, RemoveUsersFromLicense, DeactivateDevices } from "aldoo-ra/Admin/ProductLicensing/api"
import { DocumentDuplicateIcon, UserMinusIcon, UsersIcon, DevicePhoneMobileIcon, TrashIcon, UserPlusIcon, InformationCircleIcon, CheckCircleIcon, ExclamationTriangleIcon, ClipboardDocumentListIcon, BookOpenIcon } from "@heroicons/react/24/outline"
import { getPlatformIcon } from "aldoo-ra/Admin/ProductLicensing/PlatformIcons"
import { useSearchParamValue, updateQueryParams } from "aldoo-ra/SearchParams"

const LicenseInfo = ({ license, userProfile, onLicenseUpdate }) => {
  // Get current tab from URL, default to "details"
  const urlTab = useSearchParamValue('tab', 'details')
  const [activeTab, setActiveTab] = useState(urlTab)
  const [refreshKey, setRefreshKey] = useState(0)
  const [selectedUsers, setSelectedUsers] = useState([])
  const [selectedDevices, setSelectedDevices] = useState([])
  const [userCount, setUserCount] = useState(0)
  const [deviceCount, setDeviceCount] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const { t } = Localization()
  const texts = TextBundle({ id: "activate_license" })

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Sync local state with URL parameter and clear invalid tabs
  useEffect(() => {
    const validTabs = ['details', 'users', 'devices', 'guide']
    if (urlTab && validTabs.includes(urlTab)) {
      setActiveTab(urlTab)
    } else if (urlTab && !validTabs.includes(urlTab)) {
      // Clear invalid tab parameter and select first tab
      setActiveTab('details')
      updateQueryParams({ tab: null })
    }
  }, [urlTab])

  // Handle tab change and update URL
  const handleTabChange = (newTab) => {
    setActiveTab(newTab)
    updateQueryParams({ tab: newTab })
  }

  // Use requestFields to pass licenseId to the API methods
  // This avoids wrapping the API methods and prevents infinite re-renders
  const requestFields = useMemo(() => ({
    licenseId: license._id
  }), [license._id])

  // Create stable onDataLoaded callbacks
  // Note: Don't call onLicenseUpdate on every data load as it causes infinite loops
  // Only call onLicenseUpdate when actual changes happen (in action handlers)
  const handleUsersDataLoaded = useCallback((data) => {
    // Update user count for any future use
    setUserCount(data?.length || 0)
    // Return the data so DataTable can display it
    return data
  }, [])

  const handleDevicesDataLoaded = useCallback((data) => {
    // Update device count for header display
    setDeviceCount(data?.length || 0)
    // Return the data so DataTable can display it
    return data
  }, [license._id, requestFields])

  // Handle copying license code to clipboard
  const copyLicenseCode = async () => {
    try {
      await navigator.clipboard.writeText(license.code)
      showNotification({
        message: texts?.license_code_copied,
        className: "bg-success text-white"
      })
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = license.code
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)

      showNotification({
        message: texts?.license_code_copied,
        className: "bg-success text-white"
      })
    }
  }

  // Handle device deactivation
  const handleDeactivateDevice = (deviceId) => {
    const device = license.devices?.find(d => d.deviceId === deviceId || d.deviceID === deviceId)
    const deviceName = device?.name || device?.deviceName || deviceId || "Unknown Device"

    showPromptDialog({
      title: texts?.deactivate_device_title || "Deactivate Device",
      message: texts?.deactivate_device_message ?
        t(texts.deactivate_device_message, {deviceName}) :
        `Are you sure you want to deactivate "${deviceName}"? This will free up a seat for other users.`,
      confirmText: texts?.confirm_deactivate || "Deactivate",
      confirmVariant: "warning",
      onConfirm: async () => {
        try {
          const response = await DeactivateDevice({
            licenseId: license._id,
            deviceId: deviceId
          })

          if (response.result) {
            showNotification({
              message: texts?.deactivation_success ?
                t(texts.deactivation_success, {deviceName}) :
                `Device "${deviceName}" deactivated successfully`,
              className: "bg-success text-white",
            })
            setRefreshKey(prev => prev + 1)
            if (onLicenseUpdate) {
              onLicenseUpdate()
            }
          } else {
            showNotification({
              message: response.message || texts?.deactivation_failed || "Failed to deactivate device",
              className: "bg-error text-white",
            })
          }
        } catch (error) {
          console.error('Device deactivation error:', error)
          showNotification({
            message: texts?.deactivation_error || "Error deactivating device",
            className: "bg-error text-white",
          })
        }
      },
    })
  }

  // Handle removing user from license
  const handleRemoveUser = (user, isOwner = false) => {
    // Don't allow removing users if current user is not the license owner
    if (!license.isOwner) {
      return
    }

    const userType = isOwner ? "Primary Owner" : "Secondary User"
    const userTypeLower = isOwner ? "primary owner" : "secondary user"
    const userName = user?.name || user?.email || "Unknown User"

    showPromptDialog({
      title: t(texts?.remove_user_dialog_title, { userType }) || `Remove ${userType}`,
      message: isOwner ?
        t(texts?.remove_owner_dialog_message, { userTypeLower, userName }) || `Are you sure you want to remove ${userName} as the ${userTypeLower}?` :
        t(texts?.remove_user_dialog_message, { userTypeLower, userName }) || `Are you sure you want to remove ${userName} as a ${userTypeLower}?`,
      confirmText: texts?.remove_confirm_text || "Remove",
      confirmVariant: "warning",
      onConfirm: async () => {
        try {
          const response = await RemoveUserFromLicense({
            licenseId: license._id,
            userId: user._id,
          })

          if (response.result) {
            showNotification({
              message: t(texts?.remove_user_success, { userType }) || `${userType} removed successfully`,
              className: "bg-success text-white",
            })
            setRefreshKey(prev => prev + 1)
            // Refresh license data
            if (onLicenseUpdate) {
              onLicenseUpdate()
            }
          } else {
            showNotification({
              message: response.message || response.error || t(texts?.remove_user_failed, { userTypeLower }) || `Failed to remove ${userTypeLower}`,
              className: "bg-error text-white",
            })
          }
        } catch (error) {
          console.error(`Error removing user:`, error)
          showNotification({
            message: t(texts?.remove_user_error, { userTypeLower }) || `Error removing ${userTypeLower}`,
            className: "bg-error text-white",
          })
        }
      },
    })
  }

  // Handle bulk user removal
  const handleBulkRemoveUsers = () => {
    if (selectedUsers.length === 0) return

    const userCount = selectedUsers.length
    const userText = userCount === 1 ? (texts?.user_singular || "user") : (texts?.user_plural || "users")

    showPromptDialog({
      title: t(texts?.remove_user_count_dialog_title, { userCount, userText }) || `Remove ${userCount} ${userText}`,
      message: t(texts?.remove_user_count_dialog_message, { userCount, userText }) || `Are you sure you want to remove ${userCount} ${userText} from this license?`,
      confirmText: t(texts?.remove_user_count_confirmation, { userCount, userText }) || `Remove ${userCount} ${userText}`,
      confirmVariant: "warning",
      onConfirm: async () => {
        try {
          // Use bulk removal API
          const response = await RemoveUsersFromLicense({
            licenseId: license._id,
            userIds: selectedUsers,
          })

          if (response.result && response.data) {
            const { successCount, errorCount, errors, message } = response.data

            // Show success notification with backend message
            if (successCount > 0) {
              const successText = successCount === 1 ? (texts?.user_singular || "user") : (texts?.user_plural || "users")
              showNotification({
                message: message || `Successfully removed ${successCount} ${successText}`,
                className: "bg-success text-white",
              })
            }

            // Log specific errors for debugging
            if (errors && errors.length > 0) {
              console.error('User removal errors:', errors)
            }

            // Show error notification if all failed
            if (errorCount > 0 && successCount === 0) {
              const errorText = errorCount === 1 ? (texts?.user_singular || "user") : (texts?.user_plural || "users")
              showNotification({
                message: `Failed to remove ${errorCount} ${errorText}`,
                className: "bg-error text-white",
              })
            }

            // Clear selection and refresh data
            setSelectedUsers([])
            setRefreshKey(prev => prev + 1)

            // Refresh license data if any users were removed
            if (successCount > 0 && onLicenseUpdate) {
              onLicenseUpdate()
            }
          } else {
            // Handle API error response
            showNotification({
              message: response.message || response.error || texts?.user_removal_fail || "Failed to remove users",
              className: "bg-error text-white",
            })
          }
        } catch (error) {
          console.error('Bulk user removal error:', error)
          showNotification({
            message: texts?.bulk_user_removal_error || "Error removing users",
            className: "bg-error text-white",
          })
        }
      },
    })
  }

  // Handle bulk device removal
  const handleBulkRemoveDevices = () => {
    if (selectedDevices.length === 0) return

    const deviceCount = selectedDevices.length
    const deviceText = deviceCount === 1 ? (texts?.device_singular || "device") : (texts?.device_plural || "devices")
    const seatText = deviceCount === 1 ? (texts?.seat_singular || "seat") : (texts?.seat_plural || "seats")

    showPromptDialog({
      title: texts?.remove_device_count_dialog_title ?
        t(texts.remove_device_count_dialog_title, {deviceCount, deviceText}) :
        "Deactivate",
      message: texts?.remove_device_count_dialog_message ?
        t(texts.remove_device_count_dialog_message, {deviceCount, deviceText, seatText}) :
        `Are you sure you want to deactivate ${deviceCount} ${deviceText}? This will free up ${deviceCount} ${seatText} for other users.`,
      confirmText: texts?.remove_device_count_confirmation ?
        t(texts.remove_device_count_confirmation, {deviceCount, deviceText}) :
        "Deactivate",
      confirmVariant: "warning",
      onConfirm: async () => {
        try {
          // Use bulk device removal API
          const response = await DeactivateDevices({
            licenseId: license._id,
            deviceIds: selectedDevices,
          })

          if (response.result && response.data) {
            const { successCount, errorCount, errors, message } = response.data

            // Show success notification with backend message
            if (successCount > 0) {
              const successText = successCount === 1 ? (texts?.device_singular || "device") : (texts?.device_plural || "devices")
              showNotification({
                message: message || (texts?.bulk_device_removal_success ?
                  t(texts.bulk_device_removal_success, {deviceCount: successCount, deviceText: successText}) :
                  `Successfully deactivated ${successCount} ${successText}`),
                className: "bg-success text-white",
              })
            }

            // Log specific errors for debugging
            if (errors && errors.length > 0) {
              console.error('Device removal errors:', errors)
            }

            // Show error notification if all failed
            if (errorCount > 0 && successCount === 0) {
              const errorText = errorCount === 1 ? (texts?.device_singular || "device") : (texts?.device_plural || "devices")
              showNotification({
                message: texts?.bulk_device_removal_fail_all ?
                  t(texts.bulk_device_removal_fail_all, {deviceCount: errorCount, deviceText: errorText}) :
                  `Failed to deactivate ${errorCount} ${errorText}`,
                className: "bg-error text-white",
              })
            }

            // Clear selection and refresh data
            setSelectedDevices([])
            setRefreshKey(prev => prev + 1)

            // Refresh license data if any devices were removed
            if (successCount > 0 && onLicenseUpdate) {
              onLicenseUpdate()
            }
          } else {
            // Handle API error response
            showNotification({
              message: response.message || response.error || texts?.device_removal_fail || "Failed to deactivate devices",
              className: "bg-error text-white",
            })
          }
        } catch (error) {
          console.error('Bulk device removal error:', error)
          showNotification({
            message: texts?.bulk_device_removal_error || "Error deactivating devices",
            className: "bg-error text-white",
          })
        }
      },
    })
  }

  if (!license) return null

  // Create grid configuration for license information
  const gridConfig = {
    layout: [
      {
        cells: [
          {
            type: "code",
            label: texts?.code_card || "License Code",
            value: license.code,
            action: {
              icon: <DocumentDuplicateIcon className="w-6 h-6" />,
              onClick: copyLicenseCode,
              tooltip: texts?.code_column_tooltip || "Copy to clipboard"
            }
          },
          {
            type: "text",
            label: texts?.status_card || "Status",
            value: license.status.charAt(0).toUpperCase() + license.status.slice(1),
            valueColor: license.status === 'active' ? 'text-success' :
                       license.status === 'expired' ? 'text-error' :
                       license.status === 'paused' ? 'text-warning' : 'text-primary'
          },
          {
            type: "text",
            label: texts?.seats_card || "Seats",
            value: `${license.seats} ${license.seats === 1 ? (texts?.seat_singular || 'seat') : (texts?.seat_plural || 'seats')}`
          },
          {
            type: "text",
            label: texts?.validity_card || "Validity",
            value: license.validityText,
            valueColor: license.isExpired ? 'text-error' : 'text-text'
          },
          {
            type: "text",
            label: texts?.product_name_card || "Product Name",
            value: license.storeProduct?.name || license.productId || texts?.unknown_product || "Unknown Product",
            colSpan: 2
          }
        ]
      }
    ]
  }

  const tableFooter = selectedUsers.length > 0 && license.isOwner && (
    <div className="flex justify-end items-center mt-4 p-3 bg-canvas rounded">
      <Typography variant="info_16" className="mr-4 text-text_secondary">
        {selectedUsers.length} {selectedUsers.length === 1 ? (texts?.user_singular || "user") : (texts?.user_plural || "users")} selected
      </Typography>

      <Button
        variant="secondary_s"
        className="mr-3"
        onClick={() => setSelectedUsers([])}
      >
        {texts?.deselect_btn || "Deselect"}
      </Button>

      <Button
        variant="secondary_s"
        className="text-error border-error hover:bg-error hover:text-white"
        onClick={() => handleBulkRemoveUsers()}
      >
        <TrashIcon className="w-4 h-4 mr-2" />
        {texts?.remove_btn || "Remove"}
      </Button>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* License Management Tabs */}
      <div>
        <Tabs
          variant="default"
          value={activeTab}
          onChange={handleTabChange}
          tabs={[
            {
              value: "details",
              label: (
                <div className="flex items-center gap-1 md:gap-2">
                  <ClipboardDocumentListIcon className="w-4 h-4" />

                  <Typography
                    variant="info_17_medium"
                    className="my-0"
                  >
                    {texts?.details_tab_label || "Details"}
                  </Typography>
                </div>
              )
            },
            {
              value: "users",
              label: (
                <div className="flex items-center gap-1 md:gap-2">
                  <UsersIcon className="w-4 h-4" />

                  <Typography
                    variant="info_17_medium"
                    className="my-0"
                  >
                    {texts?.users_tab_label || "Users"}
                  </Typography>
                </div>
              )
            },
            {
              value: "devices",
              label: (
                <div className="flex items-center gap-1 md:gap-2">
                  <DevicePhoneMobileIcon className="w-4 h-4" />

                  <Typography
                    variant="info_17_medium"
                    className="my-0"
                  >
                    {texts?.devices_tab_label || "Devices"}
                  </Typography>
                </div>
              )
            },
            {
              value: "guide",
              label: (
                <div className="flex items-center gap-1 md:gap-2">
                  <BookOpenIcon className="w-4 h-4" />

                  <Typography
                    variant="info_17_medium"
                    className="my-0"
                  >
                    {texts?.guide_tab_label || "Guide"}
                  </Typography>
                </div>
              )
            },
          ]}
          styleOverrides={{
            tab: "relative px-2 md:px-4 py-2 text-sm font-medium transition-all duration-200 focus:outline-none flex items-center md:gap-2"
          }}
          className="mb-4"
        />

        {/* Details Tab */}
        {activeTab === "details" && (
          <div>
            <GridSummary config={gridConfig} variant="aqua" />
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div>
            <DataTable
              variant="simple"
              apiMethod={GetLicenseUsers}
              requestFields={requestFields}
              refreshKey={refreshKey}
              searchField="name email"
              itemsPerPage={10}
              tableFooter={tableFooter}
              localUserPreferencesKey="account.licenseUsers"
              styleOverrides={{
                searchInput: "w-[70%] max-w-[350px] flex-shrink-1 ml-0 mr-4",
                headerRow: "text-left bg-paper text-text font-normal text-sm border-b border-bordered shadow-sm",
                getRowStyle: (index, item) => {
                  const isSelected = selectedUsers.includes(item._id)
                  let style = "bg-paper border-b border-bordered"

                  if (isSelected) {
                    style = "bg-primary/10 border-b border-bordered"
                  }

                  style += " hover:bg-primary_lt transition-colors duration-200 cursor-pointer"
                  return style
                },
                onRowClick: (item) => {
                  const isSelected = selectedUsers.includes(item._id)
                  if (isSelected) {
                    setSelectedUsers((prev) => prev.filter((id) => id !== item._id))
                  } else {
                    setSelectedUsers((prev) => [...prev, item._id])
                  }
                }
              }}
              renders={[
                ...(!isMobile ? [{
                  id: "select",
                  headerClassName: "w-14",
                  header: (_, items) => (
                    <Checkbox
                      variant="aqua"
                      isOn={items.length > 0 && selectedUsers.length === items.length}
                      onChange={(isSelected) => {
                        if (isSelected) {
                          setSelectedUsers(items.map((item) => item._id))
                        } else {
                          setSelectedUsers([])
                        }
                      }}
                    />
                  ),
                  render: (_, item) => (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        variant="aqua"
                        isOn={selectedUsers.includes(item._id)}
                        onChange={(isSelected) => {
                          if (isSelected) {
                            setSelectedUsers((prev) => [...prev, item._id])
                          } else {
                            setSelectedUsers((prev) => prev.filter((id) => id !== item._id))
                          }
                        }}
                      />
                    </div>
                  ),
                }] : []),
                {
                  id: "user",
                  title: texts?.user_column || "User",
                  headerClassName: `${isMobile ? 'w-[70%]' : 'w-[60%]'} text-base md:text-lg`,
                  render: (_, user) => {
                    const isPrimaryOwner = license.owner?._id === user._id
                    const isCurrentUser = userProfile?._id === user._id

                    return (
                      <div className="flex items-center space-x-2 md:space-x-4 py-3">
                        <div className={`w-8 md:w-12 h-8 md:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isPrimaryOwner ? 'bg-primary' : 'bg-gray_2'
                        }`}>
                          <Typography
                            variant="body_20_accent"
                            className="text-white text-sm"
                          >
                            {(user.name || user.email).charAt(0).toUpperCase()}
                          </Typography>
                        </div>

                        <div>
                          <Typography
                            variant="body_20_accent"
                            className="text-text font-medium my-0"
                          >
                            {user.name || user.email}

                            {isCurrentUser && (
                              <span
                                className="text-primary font-normal ml-2"
                              >
                                {texts?.you}
                              </span>
                            )}
                          </Typography>

                          {user.name && user.email && (
                            <Typography
                              variant="info_14"
                              className="text-text_secondary my-0 truncate max-w-[150px]"
                            >
                              {user.email}
                            </Typography>
                          )}
                        </div>
                      </div>
                    )
                  },
                },
                {
                  id: "role",
                  title: texts?.role_column || "Role",
                  headerClassName: "w-[90px] md:w-[150px] text-base md:text-lg",
                  render: (_, user) => {
                    const isPrimaryOwner = license.owner?._id === user._id
                    return (
                      <div className="flex items-center justify-center">
                        {isPrimaryOwner ? (
                          <span className="px-2 py-1 bg-primary text-white text-xs rounded-full whitespace-nowrap">
                            Primary Owner
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray_2 text-white text-xs rounded-full whitespace-nowrap">
                            Secondary User
                          </span>
                        )}
                      </div>
                    )
                  },
                },
                ...(license.isOwner ? [{
                  id: "actions",
                  title: "",
                  headerClassName: "w-[60px]",
                  render: (_, user) => {
                    const isPrimaryOwner = license.owner?._id === user._id
                    const isCurrentUser = userProfile?._id === user._id

                    // Don't show remove button for current user or if user can't be removed
                    if (isCurrentUser) {
                      return null
                    }

                    return (
                      <div className="flex justify-center">
                        <Button
                          variant="ghost"
                          className="p-1 text-error hover:bg-error hover:text-white"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRemoveUser(user, isPrimaryOwner)
                          }}
                        >
                          <UserMinusIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    )
                  },
                }] : []),
              ]}
              noData={
                <div className="text-center py-4">
                  <UsersIcon className="w-12 h-12 text-text_secondary mx-auto mb-4" />

                  <Typography
                    variant="info_16"
                    className="text-text_secondary"
                  >
                    {texts?.no_data || "No users found for this license"}
                  </Typography>
                </div>
              }
              onDataLoaded={handleUsersDataLoaded}
            />
          </div>
        )}

        {/* Devices Tab */}
        {activeTab === "devices" && (
          <div>
            <DataTable
              variant="compact"
              apiMethod={GetLicenseDevices}
              requestFields={requestFields}
              refreshKey={refreshKey}
              searchField="name model platform"
              showSearch={true}
              itemsPerPage={10}
              localUserPreferencesKey="account.licenseDevices"
              headerControls={
                <div className="flex items-center space-x-4">
                  {/* Seat Usage text */}
                  <Typography
                    variant="info_16"
                    className="font-medium text-text whitespace-nowrap"
                  >
                    {t(texts?.seat_usage_text, {
                      used: deviceCount || 0,
                      total: license.seats
                    })}
                  </Typography>

                  {/* Seat Usage Percentage */}
                  <Typography
                    variant="info_16"
                    className="text-text_secondary text-xs"
                  >
                    {Math.round(((deviceCount || 0) / license.seats) * 100)}%
                  </Typography>
                </div>
              }
              styleOverrides={{
                searchInput: "w-[70%] max-w-[350px] flex-shrink-1 ml-0 mr-4",
                getRowStyle: (index, item) => {
                  const isSelected = selectedDevices.includes(item._id || item.deviceId)
                  let style = "bg-paper border-b border-bordered"

                  if (isSelected) {
                    style = "bg-primary/10 border-b border-bordered"
                  }

                  style += " hover:bg-primary_lt transition-colors duration-200 cursor-pointer"
                  return style
                },
                onRowClick: (item) => {
                  const deviceId = item._id || item.deviceId
                  const isSelected = selectedDevices.includes(deviceId)
                  if (isSelected) {
                    setSelectedDevices((prev) => prev.filter((id) => id !== deviceId))
                  } else {
                    setSelectedDevices((prev) => [...prev, deviceId])
                  }
                }
              }}
              renders={[
                ...(!isMobile ? [{
                  id: "select",
                  headerClassName: "w-14",
                  header: (_, items) => (
                    <Checkbox
                      isOn={items.length > 0 && selectedDevices.length === items.length}
                      onChange={(isSelected) => {
                        if (isSelected) {
                          setSelectedDevices(items.map((item) => item._id || item.deviceId))
                        } else {
                          setSelectedDevices([])
                        }
                      }}
                    />
                  ),
                  render: (_, item) => (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        isOn={selectedDevices.includes(item._id || item.deviceId)}
                        onChange={(isSelected) => {
                          const deviceId = item._id || item.deviceId
                          if (isSelected) {
                            setSelectedDevices((prev) => [...prev, deviceId])
                          } else {
                            setSelectedDevices((prev) => prev.filter((id) => id !== deviceId))
                          }
                        }}
                      />
                    </div>
                  ),
                }] : []),
                {
                  id: "device",
                  title: texts?.device_column || "Device",
                  headerClassName: `${isMobile ? 'w-[60%]' : 'w-1/2'}`,
                  render: (_, device) => (
                    <div className="flex items-center space-x-2">
                      {getPlatformIcon(device.platform, "w-8 md:w-10 h-8 md:h-10 text-text_secondary")}

                      <div>
                        <Typography
                          variant="info_16"
                          className="font-medium text-text my-0"
                        >
                          {device.name || device.deviceName || texts?.unknown_device || "Unknown Device"}
                        </Typography>

                        {device.model && (
                          <Typography
                            variant="info_12"
                            className="text-text_secondary my-0"
                          >
                            {device.model}
                          </Typography>
                        )}
                      </div>
                    </div>
                  ),
                },
                {
                  id: "owner",
                  title: texts?.owner_column || "Owner",
                  render: (_, device) => {
                    // Use the user data provided by the API
                    const owner = device.user

                    if (owner) {
                      const isCurrentUser = userProfile?._id === owner._id
                      const isLicenseOwner = license.owner?._id === owner._id

                      return (
                        <div className="flex items-center space-x-2">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                            isLicenseOwner ? 'bg-primary' : 'bg-gray_2'
                          }`}>
                            <Typography
                              variant="info_16"
                              className="text-white font-medium text-xs"
                            >
                              {(owner.name || owner.email).charAt(0).toUpperCase()}
                            </Typography>
                          </div>

                          <div>
                            <Typography
                              variant="info_16"
                              className="text-text text-xs"
                            >
                              {owner.name || owner.email}

                              {isCurrentUser && (
                                <span className="text-primary text-xs ml-1">
                                  {texts?.you || "(You)"}
                                </span>
                              )}
                            </Typography>
                          </div>
                        </div>
                      )
                    }

                    return (
                      <Typography
                        variant="info_16"
                        className="text-text_secondary text-xs"
                      >
                        {texts?.unknown_owner || "Unknown"}
                      </Typography>
                    )
                  },
                },
              ]}
              noData={
                <div className="text-center py-8">
                  {getPlatformIcon(null, "w-12 h-12 text-text_secondary mx-auto mb-4")}
                  <Typography
                    variant="info_16"
                    className="text-text_secondary mb-2"
                  >
                    {texts?.no_devices_activated || "No devices activated yet"}
                  </Typography>

                  <Typography
                    variant="info_16"
                    className="text-text_secondary text-sm"
                  >
                    {t(texts?.device_activation_info, {
                      seatCount: license.seats,
                      deviceText: license.seats > 1 ? texts?.device_plural : texts?.device_singular
                    })}
                  </Typography>
                </div>
              }
              onDataLoaded={handleDevicesDataLoaded}
            />

            {/* Bulk actions for selected devices */}
            {selectedDevices.length > 0 && license.isOwner && (
              <div className="flex justify-end items-center mt-4 p-3 bg-canvas rounded">
                <Typography
                  variant="info_16"
                  className="mr-4 text-text_secondary"
                >
                  {t(texts?.devices_selected_text, {
                    deviceCount: selectedDevices.length,
                    deviceText: selectedDevices.length === 1 ? texts?.device_singular : texts?.device_plural
                  })}
                </Typography>

                <Button
                  variant="secondary_s"
                  className="mr-3"
                  onClick={() => setSelectedDevices([])}
                >
                  {texts?.deselect_btn || "Deselect"}
                </Button>

                <Button
                  variant="secondary_s"
                  className="text-error border-error hover:bg-error hover:text-white"
                  onClick={() => handleBulkRemoveDevices()}
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  {texts?.remove_btn || "Remove"}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Guide Tab */}
        {activeTab === "guide" && (
          <div className="grid w-full mx-auto mb-[50px] grid-cols-1 gap-3">
            <Accordion
              variant="aqua"
              title={texts?.guide_title || "License Information & Guide"}
              styleOverrides={{
                title: "w-full text-[24px] sm:text-[28px] xl:text-[32px] leading-tight tracking-custom_1 font-bold text-primary_dk text-center mb-6 lg:mb-10"
              }}
              items={[
                {
                  question: texts?.guide_understanding_params_q || "Understanding Your License Parameters",
                  answer: (
                    <div className="space-y-3">
                      <p>
                        <strong>{texts?.guide_license_code_label || "License Code:"}</strong> {texts?.guide_license_code_desc || "The unique key you use to activate the software on devices"}
                      </p>
                      <p>
                        <strong>{texts?.guide_seats_label || "Seats:"}</strong> {texts?.guide_seats_desc || "Maximum number of devices that can use this license simultaneously"}
                      </p>
                      <p>
                        <strong>{texts?.guide_status_label || "Status:"}</strong> {texts?.guide_status_desc || "Current state of your license (Active, Expired, Paused)"}
                      </p>
                      <p>
                        <strong>{texts?.guide_validity_label || "Validity:"}</strong> {texts?.guide_validity_desc || "How long the license remains valid (Lifetime or expiration date)"}
                      </p>
                      <p>
                        <strong>{texts?.guide_product_name_label || "Product Name:"}</strong> {texts?.guide_product_name_desc || "The specific software or service this license covers"}
                      </p>
                    </div>
                  )
                },
                {
                  question: texts?.guide_what_you_can_do_q || "What You Can Do On This Page",
                  answer: (
                    <div className="space-y-3">
                      <p>
                        <strong>{texts?.guide_copy_license_label || "Copy License Code:"}</strong> {texts?.guide_copy_license_desc || "Click the copy icon next to your license code to share it"}
                      </p>
                      <p>
                        <strong>{texts?.guide_manage_users_label || "Manage Users:"}</strong> {texts?.guide_manage_users_desc || "View, search, and remove users who have access to this license"}
                      </p>
                      <p>
                        <strong>{texts?.guide_manage_devices_label || "Manage Devices:"}</strong> {texts?.guide_manage_devices_desc || "View active devices and deactivate unused ones to free up seats"}
                      </p>
                      <p>
                        <strong>{texts?.guide_bulk_ops_label || "Bulk Operations:"}</strong> {texts?.guide_bulk_ops_desc || "Select multiple users or devices for efficient management"}
                      </p>
                      <p>
                        <strong>{texts?.guide_search_filter_label || "Search & Filter:"}</strong> {texts?.guide_search_filter_desc || "Use search boxes to quickly find specific users or devices"}
                      </p>
                    </div>
                  )
                },
                ...(license.seats > 1 ? [{
                  question: texts?.guide_multi_seat_q || "How Multi-Seat Licenses Work",
                  answer: (
                    <div className="space-y-3">
                      <p>
                        <strong>{texts?.guide_shared_access_label || "Shared Access:"}</strong> {texts?.guide_shared_access_desc || "Multiple people can use the same license code on their devices"}
                      </p>
                      <p>
                        <strong>{texts?.guide_seat_mgmt_label || "Seat Management:"}</strong> {texts?.guide_seat_mgmt_desc || "Each device activation counts as one seat, regardless of user"}
                      </p>
                      <p>
                        <strong>{texts?.guide_owner_priv_label || "Owner Privileges:"}</strong> {texts?.guide_owner_priv_desc || "The license owner can manage all users and devices"}
                      </p>
                      <p>
                        <strong>{texts?.guide_user_perms_label || "User Permissions:"}</strong> {texts?.guide_user_perms_desc || "Regular users can activate devices but cannot manage others"}
                      </p>
                    </div>
                  )
                }] : []),
                {
                  question: texts?.guide_roles_perms_q || "User Roles & Permissions",
                  answer: (
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-base mb-2">
                          {t(texts?.guide_license_owner_title || "License Owner {{youTag}}", {
                            youTag: license.isOwner ? (texts?.you || "(You)") : ""
                          })}
                        </h4>
                        <div className="space-y-2 ml-4">
                          <p>{texts?.guide_owner_perm_1 || "• Full control over the license and all users"}</p>
                          <p>{texts?.guide_owner_perm_2 || "• Can add/remove users and manage their devices"}</p>
                          <p>{texts?.guide_owner_perm_3 || "• Can transfer ownership to another user"}</p>
                          <p>{texts?.guide_owner_perm_4 || "• Receives notifications about license status"}</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-base mb-2">
                          {t(texts?.guide_license_user_title || "License User {{youTag}}", {
                            youTag: !license.isOwner ? (texts?.you || "(You)") : ""
                          })}
                        </h4>
                        <div className="space-y-2 ml-4">
                          <p>{texts?.guide_user_perm_1 || "• Can activate the license on their own devices"}</p>
                          <p>{texts?.guide_user_perm_2 || "• Can view other users and devices (read-only)"}</p>
                          <p>{texts?.guide_user_perm_3 || "• Can leave the license voluntarily"}</p>
                          <p>{texts?.guide_user_perm_4 || "• Cannot manage other users or their devices"}</p>
                        </div>
                      </div>
                    </div>
                  )
                },
                {
                  question: texts?.guide_security_notes_q || "Important Security & Usage Notes",
                  answer: (
                    <div className="space-y-3">
                      <p>
                        <strong>{texts?.guide_keep_secure_label || "Keep License Secure:"}</strong> {texts?.guide_keep_secure_desc || "Only share your license code with trusted users"}
                      </p>
                      <p>
                        <strong>{texts?.guide_seat_limits_label || "Seat Limits Enforced:"}</strong> {texts?.guide_seat_limits_desc || "New device activations will fail if all seats are occupied"}
                      </p>
                      <p>
                        <strong>{texts?.guide_user_removal_label || "User Removal Impact:"}</strong> {texts?.guide_user_removal_desc || "Removing a user also deactivates all their devices"}
                      </p>
                      <p>
                        <strong>{texts?.guide_device_mgmt_label || "Device Management:"}</strong> {texts?.guide_device_mgmt_desc || "Regularly review and remove inactive devices to optimize usage"}
                      </p>
                      <p>
                        <strong>{texts?.guide_license_sharing_label || "License Sharing:"}</strong> {texts?.guide_license_sharing_desc || "Ensure all users comply with your software's terms of service"}
                      </p>
                    </div>
                  )
                }
              ]}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default LicenseInfo